import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, IconButton,
  Button, Tooltip, TextField, Alert, Skeleton, Switch,
  FormControlLabel, Divider, InputAdornment, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, BusinessOutlined,
  EventOutlined, PeopleAltOutlined, SearchOutlined, CheckCircle,
  Cancel, OpenInNewOutlined, CameraAltOutlined, CloseOutlined,
  GridViewOutlined, ViewListOutlined, ChevronLeftOutlined, ChevronRightOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import SlidePanel from '../../components/SlidePanel';
import axios from 'axios';

const PRIMARY    = '#1075BA';
const PAGE_SIZE  = 6;
const EMPTY      = { name: '', slug: '', email: '', website: '', description: '', industry: '', phone: '', isActive: true, logo: '' };
const API_BASE   = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('gv_admin_token')}` } });

// ── Logo uploader ─────────────────────────────────────────────────────────────
function LogoUploader({ value, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [preview, setPreview]     = useState(value || '');

  useEffect(() => { setPreview(value || ''); }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024)    { setError('File must be under 5 MB.'); return; }
    setError(''); setUploading(true);
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await axios.post(`${API_BASE}/organizations/upload-logo`, formData, {
        ...authHeader(),
        headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data.url);
      onChange(res.data.url);
    } catch (e) {
      setPreview(value || '');
      setError(e?.response?.data?.message || 'Upload failed. Check your Cloudinary config.');
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };
  const handleClear = (e) => { e.stopPropagation(); setPreview(''); onChange(''); setError(''); };

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Logo</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box onClick={() => !uploading && fileRef.current?.click()} onDrop={handleDrop} onDragOver={e => e.preventDefault()}
          sx={{ width: 88, height: 88, flexShrink: 0, borderRadius: 3, border: `2px dashed ${preview ? 'transparent' : alpha(PRIMARY, 0.3)}`, background: preview ? 'transparent' : alpha(PRIMARY, 0.03), display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'default' : 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.15s', '&:hover': uploading ? {} : { border: `2px dashed ${alpha(PRIMARY, 0.6)}`, background: alpha(PRIMARY, 0.06) } }}>
          {uploading ? (
            <CircularProgress size={28} thickness={3} sx={{ color: PRIMARY }} />
          ) : preview ? (
            <>
              <img src={preview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 10 }} />
              <Box sx={{ position: 'absolute', inset: 0, borderRadius: 'inherit', background: 'rgba(0,0,0,0.45)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                <CameraAltOutlined sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CameraAltOutlined sx={{ color: alpha(PRIMARY, 0.4), fontSize: 26, display: 'block', mx: 'auto', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.65rem', color: alpha(PRIMARY, 0.55), fontWeight: 600 }}>Upload</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, pt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>{preview ? 'Logo uploaded' : 'Click or drag to upload'}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, display: 'block', mb: 1 }}>PNG, JPG, SVG or WebP. Max 5 MB.</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()} disabled={uploading} sx={{ borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, px: 1.25, minWidth: 0 }}>
              {preview ? 'Replace' : 'Choose file'}
            </Button>
            {preview && (
              <Tooltip title="Remove logo">
                <IconButton size="small" onClick={handleClear} sx={{ border: `1px solid ${alpha('#d32f2f', 0.25)}`, borderRadius: 1.5, color: '#d32f2f', '&:hover': { background: alpha('#d32f2f', 0.06) } }}>
                  <CloseOutlined sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, py: 0.5, fontSize: '0.8rem' }} onClose={() => setError('')}>{error}</Alert>}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
    </Box>
  );
}

// ── Delete confirm ────────────────────────────────────────────────────────────
function DeleteConfirm({ org, onDeleted, onClose }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');
  const go = async () => {
    setDeleting(true); setError('');
    try { await api.delete(`/organizations/${org._id}`); onDeleted(org._id); onClose(); }
    catch (e) { setError(e?.response?.data?.message || 'Delete failed'); setDeleting(false); }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
      <Box sx={{ background: alpha('#d32f2f', 0.05), border: `1px solid ${alpha('#d32f2f', 0.15)}`, borderRadius: 2.5, p: 2.5 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          You are about to permanently delete <strong>{org?.name}</strong>. All events, forms, and submissions under this company will be orphaned. This action <strong>cannot be undone</strong>.
        </Typography>
      </Box>
      <Button variant="contained" color="error" fullWidth onClick={go} disabled={deleting} sx={{ borderRadius: 2, py: 1.2, fontWeight: 700 }}>
        {deleting ? 'Deleting…' : `Yes, delete "${org?.name}"`}
      </Button>
      <Button variant="outlined" fullWidth onClick={onClose} sx={{ borderRadius: 2 }}>Cancel</Button>
    </Box>
  );
}

// ── Card view ─────────────────────────────────────────────────────────────────
function OrgCard({ org, onEdit, onDelete, onOpen }) {
  const color = org.primaryColor || PRIMARY;
  return (
    <Card sx={{
      borderRadius: 3, border: `1px solid ${alpha(color, 0.15)}`,
      boxShadow: `0 2px 12px ${alpha(color, 0.08)}`,
      transition: 'all 0.2s ease',
      height: '100%',           // fill Grid cell for equal heights
      width: '100%',
      display: 'flex', flexDirection: 'column',
      '&:hover': { boxShadow: `0 8px 28px ${alpha(color, 0.18)}`, transform: 'translateY(-2px)' },
    }}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`, borderRadius: '12px 12px 0 0', flexShrink: 0 }} />
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 46, height: 46, borderRadius: 2.5, flexShrink: 0, background: org.logo ? '#fff' : alpha(color, 0.1), border: `1px solid ${alpha(color, 0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {org.logo
                ? <img src={org.logo} alt={org.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                : <BusinessOutlined sx={{ color, fontSize: 22 }} />
              }
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>/{org.slug}</Typography>
            </Box>
          </Box>
          <Chip size="small"
            icon={org.isActive ? <CheckCircle sx={{ fontSize: '11px !important' }} /> : <Cancel sx={{ fontSize: '11px !important' }} />}
            label={org.isActive ? 'Active' : 'Inactive'}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, background: org.isActive ? alpha('#00a882', 0.09) : alpha('#d32f2f', 0.08), color: org.isActive ? '#007a60' : '#d32f2f', border: `1px solid ${org.isActive ? alpha('#00a882', 0.25) : alpha('#d32f2f', 0.2)}`, '& .MuiChip-icon': { color: 'inherit' } }}
          />
        </Box>

        {/* Description — fixed 2-line clamp, always same space */}
        <Box sx={{ minHeight: 36, mb: 1.5 }}>
          {org.description && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5 }}>
              {org.description}
            </Typography>
          )}
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, background: alpha(PRIMARY, 0.06), borderRadius: 1.5, px: 1, py: 0.5 }}>
            <EventOutlined sx={{ fontSize: 13, color: PRIMARY }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: PRIMARY }}>{org.eventCount ?? 0}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>events</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, background: alpha('#00a882', 0.06), borderRadius: 1.5, px: 1, py: 0.5 }}>
            <PeopleAltOutlined sx={{ fontSize: 13, color: '#00a882' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00a882' }}>{org.adminCount ?? 0}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>admins</Typography>
          </Box>
          {org.industry && <Box sx={{ display: 'flex', alignItems: 'center', background: alpha('#888', 0.06), borderRadius: 1.5, px: 1, py: 0.5 }}><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{org.industry}</Typography></Box>}
        </Box>

        {/* Footer — pushed to bottom */}
        <Box sx={{ mt: 'auto' }}>
          <Divider sx={{ mb: 1.5, borderColor: alpha(PRIMARY, 0.07) }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button size="small" startIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />} onClick={() => onOpen(org)} sx={{ fontSize: '0.75rem', fontWeight: 700, color: PRIMARY, px: 1, borderRadius: 1.5, '&:hover': { background: alpha(PRIMARY, 0.07) } }}>Enter</Button>
            <Box>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(org)} sx={{ '&:hover': { color: PRIMARY } }}><EditOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(org)} sx={{ '&:hover': { color: '#d32f2f' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── List row ──────────────────────────────────────────────────────────────────
function OrgRow({ org, onEdit, onDelete, onOpen }) {
  const color = org.primaryColor || PRIMARY;
  return (
    <TableRow hover sx={{ '&:hover': { background: alpha(PRIMARY, 0.025) }, cursor: 'pointer' }}>
      <TableCell onClick={() => onOpen(org)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0, background: org.logo ? '#fff' : alpha(color, 0.1), border: `1px solid ${alpha(color, 0.18)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {org.logo
              ? <img src={org.logo} alt={org.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
              : <BusinessOutlined sx={{ color, fontSize: 18 }} />
            }
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{org.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>/{org.slug}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell onClick={() => onOpen(org)}><Typography variant="caption" sx={{ color: 'text.secondary' }}>{org.industry || '—'}</Typography></TableCell>
      <TableCell onClick={() => onOpen(org)}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip size="small" label={`${org.eventCount ?? 0} events`} sx={{ fontSize: '0.68rem', height: 20, background: alpha(PRIMARY, 0.07), color: PRIMARY, fontWeight: 600 }} />
          <Chip size="small" label={`${org.adminCount ?? 0} admins`} sx={{ fontSize: '0.68rem', height: 20, background: alpha('#00a882', 0.07), color: '#00a882', fontWeight: 600 }} />
        </Box>
      </TableCell>
      <TableCell onClick={() => onOpen(org)}>
        <Chip size="small"
          icon={org.isActive ? <CheckCircle sx={{ fontSize: '11px !important' }} /> : <Cancel sx={{ fontSize: '11px !important' }} />}
          label={org.isActive ? 'Active' : 'Inactive'}
          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, background: org.isActive ? alpha('#00a882', 0.09) : alpha('#d32f2f', 0.08), color: org.isActive ? '#007a60' : '#d32f2f', border: `1px solid ${org.isActive ? alpha('#00a882', 0.25) : alpha('#d32f2f', 0.2)}`, '& .MuiChip-icon': { color: 'inherit' } }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Enter"><IconButton size="small" onClick={() => onOpen(org)} sx={{ '&:hover': { color: PRIMARY } }}><OpenInNewOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(org)} sx={{ '&:hover': { color: PRIMARY } }}><EditOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(org)} sx={{ '&:hover': { color: '#d32f2f' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// ── Pagination bar ────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3 }}>
      <IconButton size="small" disabled={page === 1} onClick={() => onChange(page - 1)}
        sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, '&:hover:not(:disabled)': { background: alpha(PRIMARY, 0.06) } }}>
        <ChevronLeftOutlined sx={{ fontSize: 18 }} />
      </IconButton>

      {[...Array(totalPages)].map((_, i) => {
        const p = i + 1;
        const show = p === 1 || p === totalPages || Math.abs(p - page) <= 1;
        const isEllipsis = !show && (p === 2 || p === totalPages - 1);
        if (isEllipsis) return <Typography key={p} sx={{ color: 'text.secondary', fontSize: '0.8rem', px: 0.5 }}>…</Typography>;
        if (!show) return null;
        return (
          <Box key={p} onClick={() => onChange(p)}
            sx={{ width: 32, height: 32, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.82rem', fontWeight: p === page ? 700 : 500, color: p === page ? '#fff' : 'text.secondary', background: p === page ? PRIMARY : 'transparent', border: `1px solid ${p === page ? PRIMARY : alpha(PRIMARY, 0.2)}`, transition: 'all 0.15s', '&:hover': { background: p === page ? PRIMARY : alpha(PRIMARY, 0.06) } }}>
            {p}
          </Box>
        );
      })}

      <IconButton size="small" disabled={page === totalPages} onClick={() => onChange(page + 1)}
        sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, '&:hover:not(:disabled)': { background: alpha(PRIMARY, 0.06) } }}>
        <ChevronRightOutlined sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
export default function OrgsTab({ hideTitle = false }) {
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [viewMode, setViewMode]   = useState('card');   // 'card' | 'list'
  const [page, setPage]           = useState(1);
  const [panel, setPanel]         = useState(null);
  const [target, setTarget]       = useState(null);
  const [formState, setFormState] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [saving, setSaving]       = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data } = await api.get('/organizations'); setOrgs(data.data || []); }
    catch (e) { setError(e?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [search]);

  const filtered   = orgs.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()) || o.slug.includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd    = ()    => { setTarget(null); setFormState(EMPTY); setFormError(''); setSaving(false); setPanel('form'); };
  const openEdit   = (org) => { setTarget(org);  setFormState({ ...EMPTY, ...org, logo: org.logo || '' }); setFormError(''); setSaving(false); setPanel('form'); };
  const openDel    = (org) => { setTarget(org);  setPanel('delete'); };
  const closePanel = ()    => setPanel(null);
  const set        = (k, v) => setFormState(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!formState.name.trim() || !formState.slug.trim()) { setFormError('Name and slug are required'); return; }
    setSaving(true); setFormError('');
    try {
      const { data: res } = target
        ? await api.put(`/organizations/${target._id}`, formState)
        : await api.post('/organizations', formState);
      const saved = res.data;
      setOrgs(prev => target
        ? prev.map(o => o._id === saved._id ? { ...o, ...saved } : o)
        : [{ ...saved, eventCount: 0, adminCount: 0 }, ...prev]
      );
      closePanel();
    } catch (e) { setFormError(e?.response?.data?.message || 'Save failed'); setSaving(false); }
  };

  const handleDeleted = (id) => setOrgs(prev => prev.filter(o => o._id !== id));
  const openOrg = (org) => {
    localStorage.setItem('gv_selected_org', JSON.stringify({ id: org._id, name: org.name, slug: org.slug }));
    navigate('/dashboard');
  };

  const isEdit = !!target?._id;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        {!hideTitle
          ? <Box><Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: 'text.primary' }}>Companies</Typography><Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${orgs.length} compan${orgs.length !== 1 ? 'ies' : 'y'}`}</Typography></Box>
          : <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${orgs.length} compan${orgs.length !== 1 ? 'ies' : 'y'}`}</Typography>
        }

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField placeholder="Search companies…" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 17, color: 'text.secondary' }} /></InputAdornment> }} />

          {/* View toggle */}
          <Box sx={{ display: 'flex', border: `1px solid ${alpha(PRIMARY, 0.22)}`, borderRadius: 2, overflow: 'hidden' }}>
            <Tooltip title="Card view">
              <IconButton size="small" onClick={() => setViewMode('card')}
                sx={{ borderRadius: 0, px: 1.25, py: 0.75, background: viewMode === 'card' ? alpha(PRIMARY, 0.1) : 'transparent', color: viewMode === 'card' ? PRIMARY : 'text.secondary', '&:hover': { background: alpha(PRIMARY, 0.08) } }}>
                <GridViewOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Box sx={{ width: 1, background: alpha(PRIMARY, 0.22) }} />
            <Tooltip title="List view">
              <IconButton size="small" onClick={() => setViewMode('list')}
                sx={{ borderRadius: 0, px: 1.25, py: 0.75, background: viewMode === 'list' ? alpha(PRIMARY, 0.1) : 'transparent', color: viewMode === 'list' ? PRIMARY : 'text.secondary', '&:hover': { background: alpha(PRIMARY, 0.08) } }}>
                <ViewListOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}>
            New Company
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Card view ──────────────────────────────────────────────────────── */}
      {viewMode === 'card' && (
        loading ? (
          <Grid container spacing={2.5}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} key={i} sx={{ display: 'flex' }}>
                <Card sx={{ borderRadius: 3, p: 2.5, flex: 1 }}>
                  <Skeleton height={4} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}><Skeleton variant="rectangular" width={46} height={46} sx={{ borderRadius: 2 }} /><Box sx={{ flex: 1 }}><Skeleton width="70%" /><Skeleton width="40%" height={14} /></Box></Box>
                  <Skeleton height={36} sx={{ mb: 2 }} />
                  <Skeleton height={28} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <BusinessOutlined sx={{ fontSize: 52, color: alpha(PRIMARY, 0.2), mb: 2 }} />
            <Typography color="text.secondary">{search ? 'No companies match.' : 'No companies yet.'}</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2.5} alignItems="stretch">
              {paginated.map(org => (
                <Grid item xs={12} sm={6} key={org._id} sx={{ display: 'flex' }}>
                  <OrgCard org={org} onEdit={openEdit} onDelete={openDel} onOpen={openOrg} />
                </Grid>
              ))}
            </Grid>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )
      )}

      {/* ── List view ──────────────────────────────────────────────────────── */}
      {viewMode === 'list' && (
        loading ? (
          <Card sx={{ borderRadius: 3 }}>
            <Box sx={{ p: 2.5 }}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                  <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: 1.5 }} />
                  <Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="25%" height={14} /></Box>
                  <Skeleton width={80} height={20} sx={{ borderRadius: 1 }} />
                  <Skeleton width={60} height={20} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Box>
          </Card>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <BusinessOutlined sx={{ fontSize: 52, color: alpha(PRIMARY, 0.2), mb: 2 }} />
            <Typography color="text.secondary">{search ? 'No companies match.' : 'No companies yet.'}</Typography>
          </Box>
        ) : (
          <>
            <Card sx={{ borderRadius: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Industry</TableCell>
                      <TableCell>Stats</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map(org => (
                      <OrgRow key={org._id} org={org} onEdit={openEdit} onDelete={openDel} onOpen={openOrg} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )
      )}

      {/* ── Add / Edit slide panel ─────────────────────────────────────────── */}
      <SlidePanel
        open={panel === 'form'}
        onClose={closePanel}
        title={isEdit ? 'Edit Company' : 'New Company'}
        subtitle={isEdit ? `Editing ${target?.name}` : 'Fill in the details below'}
        footer={
          <>
            <Button onClick={closePanel} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ borderRadius: 2 }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Company'}
            </Button>
          </>
        }
      >
        {formError && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{formError}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <LogoUploader value={formState.logo} onChange={v => set('logo', v)} />
          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Identity</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Company Name *" value={formState.name} onChange={e => set('name', e.target.value)} fullWidth size="small" />
              <TextField label="Slug *" value={formState.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))} fullWidth size="small" helperText="Lowercase, URL-safe — e.g. acme-events" sx={{ '& .MuiFormHelperText-root': { mx: 0 } }} />
              <TextField label="Industry" value={formState.industry || ''} onChange={e => set('industry', e.target.value)} fullWidth size="small" />
            </Box>
          </Box>
          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Contact</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Email" value={formState.email || ''} onChange={e => set('email', e.target.value)} fullWidth size="small" />
              <TextField label="Phone" value={formState.phone || ''} onChange={e => set('phone', e.target.value)} fullWidth size="small" />
              <TextField label="Website" value={formState.website || ''} onChange={e => set('website', e.target.value)} fullWidth size="small" placeholder="https://..." />
            </Box>
          </Box>
          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>About</Typography>
            <TextField label="Description" value={formState.description || ''} onChange={e => set('description', e.target.value)} fullWidth size="small" multiline rows={3} />
          </Box>
          {isEdit && (
            <>
              <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5, display: 'block' }}>Status</Typography>
                <FormControlLabel
                  control={<Switch checked={formState.isActive !== false} onChange={e => set('isActive', e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00a882' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#00a882' } }} />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{formState.isActive !== false ? 'Active' : 'Inactive'}</Typography>}
                />
              </Box>
            </>
          )}
        </Box>
      </SlidePanel>

      {/* ── Delete slide panel ─────────────────────────────────────────────── */}
      <SlidePanel open={panel === 'delete'} onClose={closePanel} title="Delete Company" subtitle="This action cannot be undone" width={420}>
        <DeleteConfirm org={target} onDeleted={handleDeleted} onClose={closePanel} />
      </SlidePanel>
    </Box>
  );
}